import { Collapse, List } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../contexts/AuthContext";
import MenuLink from "./MenuLink";
import SelectLink from "./selectLink";

interface IFunctionalities {
  functionalityId: number;
  title: string;
  name: string;
}

interface MenuProps {
  collapsed?: boolean;
}

export default function Menu({ collapsed = false }: MenuProps) {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);

  const [openGarageMenu, setOpenGarageMenu] = useState(false);
  const [openPillsMenu, setOpenPillsMenu] = useState(false);
  const [openFaqMenu, setOpenFaqMenu] = useState(false);
  const [openManualMenu, setOpenManualMenu] = useState(false);
  const [openLoyaltyMenu, setOpenLoyaltyMenu] = useState(false);

  useEffect(() => {
    if (collapsed) {
      setOpenGarageMenu(false);
      setOpenPillsMenu(false);
      setOpenFaqMenu(false);
      setOpenManualMenu(false);
      setOpenLoyaltyMenu(false);
    }
  }, [collapsed]);

  if (!user) return null;

  return (
    <List component="nav" sx={{ p: 0 }}>
      <MenuLink
        title={t("menu.home")}
        toRoute="home"
        iconKey="home"
        collapsed={collapsed}
      />

      {(user.profile?.functionalities ?? [])
        .concat([
          {
            functionalityId: 99999,
            name: "FAQ",
            title: "Perguntas Frequentes",
          },
        ])
        .sort((a: any, b: any) =>
          String(t(`menu.${a.name.toLowerCase()}`, a.name)).localeCompare(
            String(t(`menu.${b.name.toLowerCase()}`, b.name)),
          ),
        )
        .map((item: IFunctionalities) => {
          const name = item.name.toLocaleLowerCase();

          if (
            name === "models" ||
            name === "colors" ||
            name === "ownersmanual" ||
            name === "pillsordering" ||
            name === "pillscontent" ||
            name === "home" ||
            name === "eligibility"
          ) {
            return null;
          }

          if (name === "garage") {
            return (
              <React.Fragment key={item.functionalityId}>
                <SelectLink
                  title={t("menu.garage")}
                  iconKey="garage"
                  onClick={() => setOpenGarageMenu(!openGarageMenu)}
                  iconChange={openGarageMenu}
                  collapsed={collapsed}
                />
                <Collapse
                  in={openGarageMenu && !collapsed}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    <MenuLink
                      title={t("menu.models")}
                      toRoute="models"
                      iconKey="models"
                      nested
                    />
                    <MenuLink
                      title={t("menu.colors")}
                      toRoute="colors"
                      iconKey="colors"
                      nested
                    />
                    <MenuLink
                      title={t("menu.bodyshell")}
                      toRoute="bodyshell"
                      iconKey="bodyshell"
                      nested
                    />
                    <MenuLink
                      title={t("menu.ownersmanual")}
                      toRoute="ownersmanual"
                      iconKey="ownersmanual"
                      nested
                    />
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          if (name === "pills") {
            return (
              <React.Fragment key={item.functionalityId}>
                <SelectLink
                  title={t("menu.pills")}
                  iconKey="pills"
                  onClick={() => setOpenPillsMenu(!openPillsMenu)}
                  iconChange={openPillsMenu}
                  collapsed={collapsed}
                />
                <Collapse
                  in={openPillsMenu && !collapsed}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    <MenuLink
                      title={t("menu.pillsContent")}
                      toRoute="pillsContent"
                      iconKey="pillscontent"
                      nested
                    />
                    <MenuLink
                      title={t("menu.pillsordering")}
                      toRoute="pillsordering"
                      iconKey="pillsordering"
                      nested
                    />
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          if (name === "faq") {
            return (
              <React.Fragment key={item.functionalityId}>
                <SelectLink
                  title={t("menu.faq")}
                  iconKey="faq"
                  onClick={() => setOpenFaqMenu(!openFaqMenu)}
                  iconChange={openFaqMenu}
                  collapsed={collapsed}
                />
                <Collapse
                  in={openFaqMenu && !collapsed}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    <MenuLink
                      title={t("menu.faqsistem")}
                      toRoute="faq/channel"
                      iconKey="faqsistem"
                      nested
                    />
                    <MenuLink
                      title={t("menu.faqcategory")}
                      toRoute="faq/category"
                      iconKey="faqcategory"
                      nested
                    />
                    <MenuLink
                      title={t("menu.faqquestion")}
                      toRoute="faq/question"
                      iconKey="faqquestion"
                      nested
                    />
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          if (name === "loyalty") {
            return (
              <React.Fragment key={item.functionalityId}>
                <SelectLink
                  title={t("menu.loyalty")}
                  iconKey="loyalty"
                  onClick={() => setOpenLoyaltyMenu(!openLoyaltyMenu)}
                  iconChange={openLoyaltyMenu}
                  collapsed={collapsed}
                />
                <Collapse
                  in={openLoyaltyMenu && !collapsed}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    <MenuLink
                      title={t("menu.eligibility")}
                      toRoute="loyalty/eligibility"
                      iconKey="eligibility"
                      nested
                    />
                    <MenuLink
                      title={t("menu.groups")}
                      toRoute="loyalty/groups"
                      iconKey="groups"
                      nested
                    />
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          if (name === "manual") {
            return (
              <React.Fragment key={item.functionalityId}>
                <SelectLink
                  title={t("menu.cognitivo")}
                  iconKey="cognitivo"
                  onClick={() => setOpenManualMenu(!openManualMenu)}
                  iconChange={openManualMenu}
                  collapsed={collapsed}
                />
                <Collapse
                  in={openManualMenu && !collapsed}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    <MenuLink
                      title={t("menu.manual")}
                      toRoute="manual"
                      iconKey="manual"
                      nested
                    />
                    <MenuLink
                      title={t("menu.transformJson")}
                      toRoute="transformJson"
                      iconKey="transformjson"
                      nested
                    />
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <MenuLink
              key={item.title}
              title={t(`menu.${name}`, item.name)}
              toRoute={name}
              iconKey={name}
              collapsed={collapsed}
            />
          );
        })}
    </List>
  );
}
